/**
 * 应用内更新（ADR-0012）：页面本身随 Mac 自动更新；只有外壳（安装包）有新版时才需要安装。
 * 有新版就在顶部提示，点一下在应用内下载并交给系统安装器（系统会请你确认）。
 */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { inApp, latestApk, shell } from "@/shared/native";
import { Button } from "@/shared/ui";

export function useAppVersion() {
  return useQuery({
    queryKey: ["app", "version"],
    queryFn: async () => {
      const [info, latest] = await Promise.all([shell.info(), latestApk()]);
      return { info, latest, outdated: Boolean(latest && latest.versionCode > info.versionCode) };
    },
    enabled: inApp(),
    refetchInterval: 30 * 60_000,
  });
}

export function useInstallUpdate() {
  const [state, setState] = useState<"idle" | "downloading" | "permission" | "started" | "failed">("idle");
  const [message, setMessage] = useState("");
  const install = async () => {
    setState("downloading");
    try {
      const res = await shell.installUpdate({ url: `${location.origin}/downloads/zhaoxi.apk` });
      if (res.needsPermission) {
        setState("permission");
        setMessage("请在刚打开的系统设置里允许朝夕安装应用，回来再点一次“更新”。");
      } else {
        setState("started");
        setMessage("已下载，请在系统的安装界面确认更新。");
      }
    } catch (e) {
      setState("failed");
      setMessage(e instanceof Error ? e.message : "更新失败");
    }
  };
  return { install, state, message };
}

export function UpdateBanner() {
  const version = useAppVersion();
  const { install, state, message } = useInstallUpdate();
  const [hidden, setHidden] = useState(false);
  if (!version.data?.outdated || hidden) return null;
  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-2 border-b border-signal-line bg-signal-soft px-4 py-2 text-[13px]"
    >
      <span className="min-w-0 flex-1">
        朝夕安卓版有新版本（{version.data.latest?.versionName}）。{message}
      </span>
      <Button size="sm" variant="signal" busy={state === "downloading"} onClick={() => void install()}>
        更新
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setHidden(true)}>
        稍后
      </Button>
    </div>
  );
}
